// jobResources — every child collection of a job, declared once.
//
// A job has five of them (subtasks, comments, photos, prerequisites, activity)
// and they are all the same shape: list them for a job, add one, change one,
// remove one. Written as five bespoke panels that is five copies of the same
// react-query wiring, and the next one is a sixth. Declared here instead, a
// panel is a list and a form, and adding a collection is one entry below.
//
// Each entry is the REAL contract, read off server/routes/*.js rather than
// assumed — the paths are inconsistent between collections (subtasks and
// comments hang off /subtasks and /comments with a job_id in the body;
// dependencies hang off /jobs/:id) and this is the one place that has to know:
//
//   subtasks      GET  /subtasks/job/:jobId      POST /subtasks {job_id,...}
//                 PUT  /subtasks/:id             DELETE /subtasks/:id
//   comments      GET  /comments/job/:jobId      POST /comments {job_id,...}
//                 PUT  /comments/:id             DELETE /comments/:id
//   photos        GET  /media/job/:jobId         POST /media/job/:jobId (multipart)
//                 PUT  /media/:id {caption}      DELETE /media/:id
//   dependencies  GET  /jobs/:id/dependencies    POST /jobs/:id/dependencies
//                 DELETE /jobs/:id/dependencies/:depJobId
//   activity      GET  /jobs/:id/activity        (read-only)
//
// Server-side permissions differ per collection and are NOT re-implemented
// here; the screen shows what a refusal said. Two worth knowing while reading
// a panel: a worker may only tick a subtask (title/description edits are 403),
// and a comment may be edited by its author or an admin, deleted by its
// author, a manager or an admin.

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del, request, describeError } from '../http';
import { uploadInBatches, uploadSummary } from '../photoBatches';

/** How each collection is addressed. `create`/`update`/`remove` may be null. */
export const JOB_RESOURCES = {
  subtasks: {
    label: 'Work list',
    list: (jobId) => `/subtasks/job/${jobId}`,
    create: () => '/subtasks',
    body: (jobId, fields) => ({ job_id: jobId, ...fields }),
    update: (id) => `/subtasks/${id}`,
    remove: (id) => `/subtasks/${id}`,
  },
  comments: {
    label: 'Comments',
    list: (jobId) => `/comments/job/${jobId}`,
    create: () => '/comments',
    body: (jobId, fields) => ({ job_id: jobId, ...fields }),
    update: (id) => `/comments/${id}`,
    remove: (id) => `/comments/${id}`,
  },
  photos: {
    label: 'Photos',
    list: (jobId) => `/media/job/${jobId}`,
    // multipart, so it goes through `upload` rather than `create`
    create: null,
    upload: (jobId) => `/media/job/${jobId}`,
    update: (id) => `/media/${id}`,
    remove: (id) => `/media/${id}`,
  },
  dependencies: {
    label: 'Waiting on',
    list: (jobId) => `/jobs/${jobId}/dependencies`,
    create: (jobId) => `/jobs/${jobId}/dependencies`,
    body: (_jobId, fields) => fields,
    update: null,
    // the id in the path is the job depended ON, not a row id
    remove: (depJobId, jobId) => `/jobs/${jobId}/dependencies/${depJobId}`,
  },
  activity: {
    label: 'History',
    list: (jobId) => `/jobs/${jobId}/activity`,
    create: null,
    update: null,
    remove: null,
  },
};

/** The react-query key for one job's collection. */
export const jobResourceKey = (name, jobId) => ['job', String(jobId), name];

/**
 * List + mutate one child collection of a job.
 *
 *   const list = useJobResource('subtasks', job.id);
 *   list.rows            — always an array, [] while loading
 *   list.create(fields)  — resolves true, or false with list.error set
 *   list.update(id, f)   · list.remove(id) · list.uploadFiles(files)
 *
 * Mutations resolve a boolean rather than throwing: a panel's job is to show
 * the server's sentence, not to manage exceptions. `error` is that sentence.
 */
export function useJobResource(name, jobId, { enabled = true } = {}) {
  const spec = JOB_RESOURCES[name];
  if (!spec) throw new Error(`Unknown job resource: ${name}`);
  const qc = useQueryClient();
  const key = jobResourceKey(name, jobId);

  const query = useQuery({
    queryKey: key,
    queryFn: () => get(spec.list(jobId)),
    enabled: enabled && !!jobId,
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async (fn, fallback) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: key });
      return true;
    } catch (e) {
      setError(describeError(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, JSON.stringify(key), setBusy, setError]);

  const create = useCallback(
    (fields) => run(() => post(spec.create(jobId), spec.body(jobId, fields)), `Could not add that to ${spec.label.toLowerCase()}.`),
    [run, spec, jobId],
  );

  const update = useCallback(
    (id, fields) => run(() => put(spec.update(id), fields), 'Could not save that change.'),
    [run, spec],
  );

  const remove = useCallback(
    (id) => run(() => del(spec.remove(id, jobId)), 'Could not remove that.'),
    [run, spec, jobId],
  );

  /**
   * Upload a selection of files, however big the selection is.
   *
   * This built ONE FormData out of whatever the picker returned and posted it,
   * which is why a crew member reported "can't upload more than 10 at a time":
   * the eleventh file did not make the request bigger, it made the whole
   * request a 400 with nothing stored. Raising the server's cap alone would
   * only move the wall, because the real ceiling is nginx's
   * `client_max_body_size 50M` and no count clears that at 25MB a file.
   *
   * So the selection is chunked — see ../photoBatches.js for the three numbers
   * and the arithmetic. What matters here is the two properties the panel
   * depends on:
   *
   *   - it does NOT go through `run`. `run` reports a boolean, and a boolean
   *     cannot say "22 of 30". A partial upload reported as a success is the
   *     one outcome worse than the wall.
   *   - the list is invalidated WHETHER OR NOT every batch landed, because
   *     after a partial upload the photos that did land are on the server and
   *     the panel must show them. Refetching only on success is how a crew
   *     member re-uploads the ones they already sent.
   */
  const uploadFiles = useCallback(async (files, { captions, onProgress } = {}) => {
    setBusy(true);
    setError('');
    const out = await uploadInBatches(files, (batch, { from, to }) => {
      const form = new FormData();
      for (const f of batch) form.append('files', f);
      // Captions are per FILE and the batches are in order, so each request
      // carries its own slice. Sending the whole array with every batch would
      // caption the wrong photos from the second request on.
      if (captions) form.append('captions', JSON.stringify(captions.slice(from, to)));
      return request(spec.upload(jobId), { method: 'POST', body: form });
    }, { onProgress });
    await qc.invalidateQueries({ queryKey: key });
    setBusy(false);
    const sentence = uploadSummary(out);
    if (sentence) setError(sentence);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, JSON.stringify(key), spec, jobId, setBusy, setError]);

  return {
    rows: Array.isArray(query.data) ? query.data : [],
    isLoading: query.isLoading,
    loadError: query.error,
    busy,
    error,
    clearError: () => setError(''),
    create: spec.create || spec.upload ? create : null,
    update: spec.update ? update : null,
    remove: spec.remove ? remove : null,
    uploadFiles: spec.upload ? uploadFiles : null,
  };
}
